import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/ui/components/ui/button';
import dayjs from 'dayjs';
import Link from 'next/link';

import { MatchupStatusEnum } from '@/entities/matchup/constants';
import { LINEUP_PERMISSIONS, SCORES_PERMISSIONS } from '@/entities/PermissionRoles';
import { CaptainLineupsAndScoresPermissions } from '@/entities/season-division/SeasonDivisionPool';
import { Timezone } from '@/entities/TimeZones';
import { useOrganizationStore } from '@/store/organization-store';
import { usePermissionStore } from '@/store/permission-store';
import { DateUtil } from '@/utils/date';

interface UpcomingMatchCardProps {
	plannedStartDate?: string;
	homeTeam: string;
	homeTeamUuid: string;
	awayTeam: string;
	awayTeamUuid: string;
	matchupStatus: string;
	time?: string;
	venue?: string;
	buttonText?: string;
	plannedEndDate?: string;
	completed?: boolean;
	href: string;
	permission: CaptainLineupsAndScoresPermissions;
	timezone?: Timezone | null;
}

export const UpcomingMatchCard = ({
	plannedStartDate,
	homeTeam,
	awayTeam,
	matchupStatus,
	venue,
	plannedEndDate,
	href,
	permission,
	homeTeamUuid,
	awayTeamUuid,
	timezone
}: UpcomingMatchCardProps) => {
	const { teamLeagueTimeZone: tlTimeZone } = useOrganizationStore();
	const ianaTzIdentifier = timezone?.ianaTzIdentifier || tlTimeZone?.ianaTzIdentifier;

	const updatedHref = timezone ? `${href}?ianaTzIdentifier=${timezone?.ianaTzIdentifier}&abbreviation=${timezone?.abbreviation}` : href;

	const localDate = dayjs.utc(plannedStartDate).tz(ianaTzIdentifier || 'UTC');
	const permissionsStore = usePermissionStore();
	const isToday = localDate.isSame(dayjs().tz(ianaTzIdentifier || 'UTC'), 'day');
	const formattedDate = isToday ? 'Today' : DateUtil.formatUTCInTimezone(plannedStartDate, 'MMM D YYYY', ianaTzIdentifier);
	const formattedTime = DateUtil.formatUTCInTimezone(plannedStartDate, 'h:mm A', ianaTzIdentifier);
	const formattedLineupDeadline = `by ${DateUtil.formatUTCInTimezone(plannedEndDate, 'ddd MMM D h:mm A', ianaTzIdentifier)} ${timezone?.abbreviation || tlTimeZone?.abbreviation || ''}`;

	const isTeamCaptain = permissionsStore.teamIds.includes(homeTeamUuid) || permissionsStore.teamIds.includes(awayTeamUuid);
	const canSetLineup = permissionsStore.gamedayAccess || (LINEUP_PERMISSIONS.some((v) => v === permission) && isTeamCaptain);
	const canSetScores = permissionsStore.gamedayAccess || (SCORES_PERMISSIONS.some((v) => v === permission) && isTeamCaptain);

	return (
		<div className="rounded-lg bg-gray-50 px-4 py-2 text-sm shadow-sm">
			<div className="text-xs text-gray-500 grid-cols-6">
				{isToday ? `Today - ${localDate.format('MMM D YYYY')} ${formattedTime}` : `${formattedDate} ${formattedTime}`}{' '}
				{timezone?.abbreviation || tlTimeZone?.abbreviation || ''}
			</div>
			<div className="grid grid-cols-6 md:gap-4 justify-between">
				<div className="col-span-6 md:col-span-2 flex flex-col sm:flex-row items-start gap-1 pt-3 text-sm font-semibold text-gray-700 ">
					<div className="flex items-center">
						<span className="mr-2 inline-block size-[6px] rounded-full bg-orange-500" />
						<span>{awayTeam}</span>
						<div className="flex pl-1 text-gray-700 sm:hidden">vs</div>
					</div>

					<div className="hidden text-gray-500 sm:flex">@</div>

					<div className="flex items-center">
						<span>{homeTeam}</span>
					</div>
				</div>

				{venue ? (
					<div className="col-span-6 md:col-span-2 flex items-center gap-1 pt-4 text-xs text-gray-700 md:pt-6 md:hidden mb-2 md:mb-0">
						<FontAwesomeIcon icon={faLocationDot} className="h-[20px] w-[14px]" />
						<span className="text-gray-700">{venue}</span>
					</div>
				) : null}

				{venue ? (
					<div className="col-span-6 md:col-span-2 hidden md:flex items-center justify-end gap-1 text-xs text-gray-700">
						<FontAwesomeIcon icon={faLocationDot} className="h-[20px] w-[14px] shrink-0" />
						<span>{venue}</span>
					</div>
				) : null}

				<div className="col-span-6 md:col-span-2 flex flex-col justify-center items-center md:items-end mt-5 sm:mt-0">
					<div className="md:max-w-[200px]">
						{(matchupStatus === MatchupStatusEnum.UNSCHEDULED_MATCHUP_STATUS ||
							matchupStatus === MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS ||
							matchupStatus === MatchupStatusEnum.SCHEDULED_WAITING_FOR_HOME_TEAM_LINEUP ||
							matchupStatus === MatchupStatusEnum.SCHEDULED_WAITING_FOR_AWAY_TEAM_LINEUP) && (
							<Link href={updatedHref} prefetch={false}>
								<Button
									className="!sm:px-[60px] !h-[26px] cursor-pointer !rounded-md !px-[10px] !py-4 text-xs font-medium shadow-md w-full disabled:cursor-not-allowed"
									disabled={!canSetLineup}
									title={!canSetLineup ? "You don't have permission to set the lineup" : ''}
								>
									Set line up
								</Button>
							</Link>
						)}

						{(matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ||
							matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS) && (
							<Link href={updatedHref} prefetch={false}>
								<Button className="!md:px-[60px] !h-[26px] cursor-pointer !rounded-md !px-[10px] !py-4 text-xs font-medium shadow-md sm:w-full">
									{!canSetScores ? 'View' : 'Enter'} scores
								</Button>
							</Link>
						)}

						{matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS && (
							<div className="flex items-center">
								<span className="inline-block size-2 rounded-full bg-[#099250]" />
								<p className="pl-2 text-[#099250]">Match completed</p>
							</div>
						)}

						{formattedLineupDeadline ? (
							<div className="min-w-[80px] pt-2 text-xs text-gray-700 md:w-full">{formattedLineupDeadline}</div>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
};
